import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { mdiPlus } from '@mdi/js'
import { apiClient } from '../../api/client'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'
import { AppIcon } from '../ui/icon'
import { Alert, AlertDescription } from '../ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { UserSelect } from '../UserSelect'
import { useCan } from '../../contexts/PermissionsContext'
import { useFormatters } from '../../lib/format'
import { guaranteeConflicts, useCreateLeaseGuarantee, useLeaseGuarantees, type GuaranteeConflict } from '../../api/leaseRent'
import { conflictLabel, GuaranteeConflictConfirmDialog } from './GuaranteeConflictConfirmDialog'

const UNITS = ['DAY', 'MONTH', 'YEAR'] as const

/**
 * The guarantees of a lease (spec 2026-10-25-08 step 9): its guarantors, its
 * guarantee contracts (children of the lease) with their claim reporting
 * period, the student or apprentice flag, and the rules they break.
 */
export function GuaranteesCard({ org, contractUid }: { org: string; contractUid: string }) {
  const { t, i18n } = useTranslation('contracts')
  const { formatDate } = useFormatters()
  const canWrite = useCan('contracts.write')
  const queryClient = useQueryClient()
  const { data } = useLeaseGuarantees(org, contractUid)
  const create = useCreateLeaseGuarantee()
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ type_code: '', provider: '', start_date: '', reference: '', period_value: '', period_unit: 'MONTH' })
  const [error, setError] = useState('')
  const [conflicts, setConflicts] = useState<GuaranteeConflict[] | null>(null)

  if (!data) return null

  const refresh = () =>
    queryClient.invalidateQueries({
      predicate: (q) => {
        const path = (q.queryKey as [string, string])[1]
        return typeof path === 'string' && path.includes('/guarantees')
      },
    })

  const setStudent = async (value: boolean) => {
    if (!data.tenant_student_or_apprentice_property_uid) return
    await apiClient.PUT('/api/v1/organizations/{orgCode}/contracts/{uid}/properties/{propertyUid}', {
      params: { path: { orgCode: org, uid: contractUid, propertyUid: data.tenant_student_or_apprentice_property_uid } },
      body: { value } as never,
    })
    await refresh()
  }

  const save = async (confirm: boolean) => {
    setError('')
    const body: Record<string, unknown> = {
      type_code: form.type_code, provider_user_uid: form.provider, start_date: form.start_date,
      confirm_guarantee_conflict: confirm,
    }
    if (form.reference.trim()) body.policy_number = form.reference.trim()
    if (form.period_value) body.claims_reporting_period = { value: Number(form.period_value), unit: form.period_unit }
    try {
      await create.mutateAsync({ params: { path: { orgCode: org, contractUid } }, body: body as never })
      setConflicts(null)
      setAdding(false)
      setForm({ type_code: '', provider: '', start_date: '', reference: '', period_value: '', period_unit: 'MONTH' })
    } catch (e) {
      const c = guaranteeConflicts(e)
      if (c) {
        setConflicts(c)
        return
      }
      const p = e as { title?: string; fields?: { name: string; message: string }[] }
      setError(p.fields?.length ? p.fields.map((f) => `${f.name}: ${f.message}`).join(', ') : (p.title ?? t('lease.guarantees.save_failed')))
    }
  }

  const period = (v: unknown) => {
    const d = v as { value?: number; unit?: string } | null
    return d?.value ? `${d.value} ${t(`lease.guarantees.units.${(d.unit ?? '').toUpperCase()}` as any, { defaultValue: d.unit })}` : null
  }

  return (
    <Card data-testid="lease-guarantees-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">{t('lease.guarantees.title')}</CardTitle>
        {canWrite && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="lease-guarantee-add-button">
            <AppIcon path={mdiPlus} size="sm" className="mr-2" />{t('lease.guarantees.add')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {data.conflicts.length > 0 && (
          <Alert variant="warning" data-testid="lease-guarantee-conflict-banner">
            <AlertDescription>
              {data.conflicts.map((c) => (
                <div key={c.rule_uid}>
                  <p>{conflictLabel(c, i18n.language)}</p>
                  {c.legal_basis && <p className="text-xs text-muted-foreground">{c.legal_basis}</p>}
                </div>
              ))}
            </AlertDescription>
          </Alert>
        )}
        <div>
          <p className="mb-1 font-medium">{t('lease.guarantees.guarantors')}</p>
          {data.guarantors.length === 0 ? (
            <p className="text-muted-foreground">{t('lease.guarantees.no_guarantor')}</p>
          ) : (
            <ul className="space-y-1">
              {data.guarantors.map((g) => (
                <li key={g.uid} data-testid={`lease-guarantor-row-${g.uid}`}>
                  <Link to="/$org/users/$userUid" params={{ org, userUid: g.user_uid }} className="underline">{g.name ?? g.user_uid}</Link>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="mb-1 font-medium">{t('lease.guarantees.contracts')}</p>
          {data.guarantee_contracts.length === 0 ? (
            <p className="text-muted-foreground">{t('lease.guarantees.no_contract')}</p>
          ) : (
            <ul className="space-y-2">
              {data.guarantee_contracts.map((g) => (
                <li key={g.uid} className="flex flex-wrap items-center gap-2" data-testid={`lease-guarantee-row-${g.uid}`}>
                  <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: g.uid }} className="underline">
                    {t(`lease.guarantees.types.${g.type_code}` as any, { defaultValue: g.name ?? g.type_code })}
                  </Link>
                  {g.provider && <span className="text-muted-foreground">{g.provider.name}</span>}
                  {g.policy_number && <span className="tabular-nums">{g.policy_number}</span>}
                  {g.start_date && <span className="text-muted-foreground">{formatDate(g.start_date)}</span>}
                  {period(g.claims_reporting_period) ? (
                    <Badge variant="outline">{t('lease.guarantees.reporting', { period: period(g.claims_reporting_period) })}</Badge>
                  ) : (
                    <Badge variant="warning" data-testid="lease-guarantee-missing-deadline-badge">{t('lease.guarantees.reporting_missing')}</Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <label className="flex items-center gap-2">
          <Checkbox checked={data.tenant_student_or_apprentice} disabled={!canWrite || !data.tenant_student_or_apprentice_property_uid}
            onCheckedChange={(c) => void setStudent(!!c)} data-testid="lease-guarantee-student-checkbox" />
          {t('lease.guarantees.student')}
        </label>
      </CardContent>

      {adding && (
        <Dialog open onOpenChange={(open) => !open && setAdding(false)}>
          <DialogContent data-testid="lease-guarantee-dialog">
            <DialogHeader><DialogTitle>{t('lease.guarantees.add')}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="col-span-2 space-y-1">
                <Label>{t('lease.guarantees.type')}</Label>
                <Select value={form.type_code} onValueChange={(v) => setForm({ ...form, type_code: v })}>
                  <SelectTrigger data-testid="lease-guarantee-type-select"><SelectValue placeholder={t('lease.guarantees.type')} /></SelectTrigger>
                  <SelectContent>
                    {data.guarantee_type_codes.map((code) => (
                      <SelectItem key={code} value={code} data-testid={`lease-guarantee-type-${code}`}>
                        {t(`lease.guarantees.types.${code}` as any, { defaultValue: code })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1">
                <Label>{t('lease.guarantees.provider')}</Label>
                <UserSelect org={org} value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}
                  placeholder={t('lease.guarantees.provider')} data-testid="lease-guarantee-provider-select" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lease-guarantee-start">{t('lease.guarantees.start_date')}</Label>
                <Input id="lease-guarantee-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="lease-guarantee-start-input" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lease-guarantee-reference">{t('lease.guarantees.reference')}</Label>
                <Input id="lease-guarantee-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  data-testid="lease-guarantee-reference-input" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lease-guarantee-deadline">{t('lease.guarantees.reporting_period')}</Label>
                <Input id="lease-guarantee-deadline" inputMode="numeric" value={form.period_value}
                  onChange={(e) => setForm({ ...form, period_value: e.target.value })} data-testid="lease-guarantee-deadline-input" />
              </div>
              <div className="space-y-1">
                <Label>{t('lease.guarantees.unit')}</Label>
                <Select value={form.period_unit} onValueChange={(v) => setForm({ ...form, period_unit: v })}>
                  <SelectTrigger data-testid="lease-guarantee-deadline-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => <SelectItem key={u} value={u}>{t(`lease.guarantees.units.${u}` as any)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {error && (
              <Alert variant="destructive" data-testid="lease-guarantee-error"><AlertDescription>{error}</AlertDescription></Alert>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setAdding(false)} data-testid="lease-guarantee-cancel-button">{t('lease.guarantees.cancel')}</Button>
              <Button onClick={() => void save(false)} disabled={create.isPending || !form.type_code || !form.provider || !form.start_date}
                data-testid="lease-guarantee-save-button">{t('lease.guarantees.save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {conflicts && (
        <GuaranteeConflictConfirmDialog conflicts={conflicts} onCancel={() => setConflicts(null)} onConfirm={() => void save(true)} />
      )}
    </Card>
  )
}
